import React, { useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, Badge } from "react-bootstrap";

export default function FilterModal({
  show,
  onHide,
  applyFilter,
  removeFilter,
  generateSalary,
  month,
  setMonth,
  year,
  setYear,
  clientFilter = [],
  setClientFilter,
  statusFilter,
  setStatusFilter,
  userSourceFilter,
  setUserSourceFilter,
  providerFilter = [],
  setProviderFilter,
  staffTypeFilter = [],
  setStaffTypeFilter,
  isFinalizedFilter,
  setIsFinalizedFilter,
  isPaidFilter,
  setIsPaidFilter,
  uniqueProviders = [],
  uniqueStaffTypes = [],
  apidata = [],
}) {
  const [clientSearch, setClientSearch] = useState("");
  const [providerSearch, setProviderSearch] = useState("");
  const [staffSearch, setStaffSearch] = useState("");

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const clients = useMemo(() => {
    const names = apidata.flatMap((item) =>
      Array.isArray(item?.id_user?.id_client)
        ? item.id_user.id_client.map((client) => client?.name?.trim())
        : [],
    );

    return [...new Set(names.filter(Boolean))].sort((a, b) =>
      a.localeCompare(b),
    );
  }, [apidata]);

  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: 10 },
    (_, index) => currentYear - 5 + index,
  );

  const toggleValue = (value, selected, setSelected) => {
    setSelected(
      selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value],
    );
  };

  const renderCheckboxList = ({
    title,
    items,
    selected,
    setSelected,
    search,
    setSearch,
    emptyText,
    includeAll = false,
  }) => {
    const visibleItems = items.filter((item) =>
      item.toLowerCase().includes(search.trim().toLowerCase()),
    );

    return (
      <Form.Group className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Form.Label className="fw-semibold mb-0">{title}</Form.Label>
          <Badge bg="light" text="dark">
            {selected.length} selected
          </Badge>
        </div>

        <Form.Control
          type="search"
          size="sm"
          className="mb-2"
          placeholder={`Search ${title.toLowerCase()}...`}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        {includeAll && (
          <Form.Check
            type="checkbox"
            label="All Staff Types"
            checked={selected.length === 0}
            onChange={() => setSelected([])}
            className="fw-semibold mb-2"
          />
        )}

        <div
          className="border rounded-2 p-2"
          style={{ maxHeight: 145, overflowY: "auto" }}
        >
          {visibleItems.length ? (
            visibleItems.map((item) => (
              <Form.Check
                key={item}
                type="checkbox"
                label={item}
                checked={selected.includes(item)}
                onChange={() => toggleValue(item, selected, setSelected)}
                className="mb-1"
              />
            ))
          ) : (
            <div className="text-muted small py-2">
              {emptyText || "No options found."}
            </div>
          )}
        </div>
      </Form.Group>
    );
  };

  const handleRemoveFilters = () => {
    setClientSearch("");
    setProviderSearch("");
    setStaffSearch("");
    removeFilter();
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <div>
          <Modal.Title className="fw-semibold">Salary Filters</Modal.Title>
          <div className="text-muted small">
            Choose the period and narrow down salary records.
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <div className="mb-3">
            <div className="fw-semibold mb-2">Salary Period</div>
            <Row>
              <Col sm={6} className="mb-2">
                <Form.Label>Month</Form.Label>
                <Form.Select
                  value={month ?? ""}
                  onChange={(event) => setMonth(event.target.value)}
                >
                  <option value="">Select month</option>
                  {months.map((name, index) => (
                    <option key={name} value={String(index + 1)}>
                      {name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col sm={6} className="mb-2">
                <Form.Label>Year</Form.Label>
                <Form.Select
                  value={year ?? ""}
                  onChange={(event) => setYear(event.target.value)}
                >
                  <option value="">Select year</option>
                  {years.map((value) => (
                    <option key={value} value={String(value)}>
                      {value}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
          </div>

          <hr />

          {renderCheckboxList({
            title: "Clients",
            items: clients,
            selected: clientFilter,
            setSelected: setClientFilter,
            search: clientSearch,
            setSearch: setClientSearch,
            emptyText: "No clients found in the loaded salary data.",
          })}

          {renderCheckboxList({
            title: "Third Party Providers",
            items: uniqueProviders,
            selected: providerFilter,
            setSelected: setProviderFilter,
            search: providerSearch,
            setSearch: setProviderSearch,
            emptyText:
              "No third-party providers found in the loaded salary data.",
          })}

          {renderCheckboxList({
            title: "Staff Type",
            items: uniqueStaffTypes,
            selected: staffTypeFilter,
            setSelected: setStaffTypeFilter,
            search: staffSearch,
            setSearch: setStaffSearch,
            emptyText: "No staff types found in the loaded salary data.",
            includeAll: true,
          })}

          <hr />
          <div className="fw-semibold mb-2">Other Filters</div>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>User Source</Form.Label>
                <Form.Select
                  value={userSourceFilter}
                  onChange={(event) => setUserSourceFilter(event.target.value)}
                >
                  <option value="">All Sources</option>
                  <option value="InHouse">InHouse</option>
                  <option value="ThirdParty">ThirdParty</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Is Finalized</Form.Label>
                <Form.Select
                  value={isFinalizedFilter}
                  onChange={(event) => setIsFinalizedFilter(event.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Is Paid</Form.Label>
                <Form.Select
                  value={isPaidFilter}
                  onChange={(event) => setIsPaidFilter(event.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={handleRemoveFilters}>
          Clear All
        </Button>

        <div className="d-flex flex-wrap gap-2">
          <Button variant="outline-secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="success" onClick={generateSalary}>
            Generate Salary
          </Button>
          <Button variant="primary" onClick={applyFilter}>
            Apply Filters
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
